import { FC, useState } from 'react';
import { Avatar, Flex, Stack, Text, Tooltip, Button } from '@chakra-ui/react';
import {
  CFR_CHART_KEYS,
  COMMIT_CHART_KEYS,
  DEPLOY_CHART_KEYS,
  GOAL_METRIC_GIT_KEYS,
  GOAL_METRIC_JIRA_KEYS,
  ISSUE_CHART_KEYS,
  PR_AGEING_CHARTS,
  PR_CHART_KEYS,
  PR_HEALTH_INDEX_CHARTS,
  REVIEW_RESPONSIVENESS_INDEX_CHARTS,
  WORK_BREAKDOWN_CHARTS,
  LOC_VS_PR_CYCLE_TIME_CHARTS,
  ISSUE_LIFECYCLE_DIST_CHARTS,
  truncateText,
} from '../../../../shared/utils';
import { Link } from 'react-router-dom';

interface InfoColumnCellProps {
  item: any;
  row: any;
  heading: string;
  currentChartMeta: any;
}

export const InfoColumnCell: FC<InfoColumnCellProps> = ({
  item,
  row,
  heading,
  currentChartMeta,
}) => {
  const [expandedAuthors, setExpandedAuthors] = useState<
    Record<string, boolean>
  >({});

  return (
    <Flex direction="column" gap={3}>
      <Flex justify="space-between" align="flex-start" gap={4}>
        <Stack spacing={1.5} maxW="full">
          {/meeting/i.test(heading) ? (
            <Tooltip hasArrow label={item?.title}>
              <Text
                fontSize="sm"
                fontFamily="heading"
                color="gray.800"
                fontWeight="semibold"
                noOfLines={2}
                wordBreak="break-word"
              >
                {item?.title}
              </Text>
            </Tooltip>
          ) : /document updated/i.test(heading) ||
            currentChartMeta?.chartKey === 'COMMIT_COUNT' ? (
            <Tooltip hasArrow label={item?.title}>
              <Link
                to={item?.externalUrl || item?.externalLink}
                target="_blank"
                rel="noopener"
              >
                <Text
                  fontSize="sm"
                  fontFamily="heading"
                  color="primary.500"
                  fontWeight="semibold"
                  textDecoration="underline"
                  noOfLines={2}
                  wordBreak="break-word"
                >
                  {item?.title}
                </Text>
              </Link>
            </Tooltip>
          ) : (
            <Tooltip hasArrow label={item?.title}>
              <Link
                to={`/timeline/${
                  [...CFR_CHART_KEYS].includes(currentChartMeta?.chartKey)
                    ? 'cfr'
                    : [
                        ...PR_CHART_KEYS,
                        ...GOAL_METRIC_GIT_KEYS,
                        ...COMMIT_CHART_KEYS,
                        ...PR_AGEING_CHARTS,
                        ...PR_HEALTH_INDEX_CHARTS,
                        ...REVIEW_RESPONSIVENESS_INDEX_CHARTS,
                        ...WORK_BREAKDOWN_CHARTS,
                        ...LOC_VS_PR_CYCLE_TIME_CHARTS,
                      ].includes(currentChartMeta?.chartKey)
                    ? 'pr'
                    : [
                        ...ISSUE_CHART_KEYS,
                        ...GOAL_METRIC_JIRA_KEYS,
                        ...ISSUE_LIFECYCLE_DIST_CHARTS,
                      ].includes(currentChartMeta?.chartKey)
                    ? 'issue'
                    : [...DEPLOY_CHART_KEYS].includes(
                        currentChartMeta?.chartKey
                      )
                    ? 'build'
                    : 'issue'
                }/${item?.id || item.commitId}`}
              >
                <Text
                  fontSize="sm"
                  fontFamily="heading"
                  color="blue.700"
                  fontWeight="medium"
                  textDecoration="underline"
                  noOfLines={2}
                  wordBreak="break-word"
                >
                  {item?.title}
                </Text>
              </Link>
            </Tooltip>
          )}

          {item?.summary &&
            item?.ticketUrl &&
            currentChartMeta?.chartKey !== 'TEAM_HYGIENE' && (
              <Link to={`${item?.ticketUrl}`}>
                <Text
                  fontSize="sm"
                  fontFamily="body"
                  color="gray.700"
                  fontWeight="normal"
                  textDecoration="underline"
                  noOfLines={2}
                  wordBreak="break-word"
                >
                  {truncateText(item?.summary, 120)}
                </Text>
              </Link>
            )}

          {item?.sprintName && item?.sprintId && (
            <Link to={`/sprint/${item?.sprintId}`}>
              <Text
                fontSize="xs"
                fontFamily="heading"
                color="text.secondary"
                fontWeight="medium"
                textDecoration="underline"
              >
                {item?.sprintName}
              </Text>
            </Link>
          )}
        </Stack>
      </Flex>

      <Stack spacing={1.5}>
        {item?.authors && item.authors.length > 0 && (
          <Flex direction="column" gap={1} mt={1}>
            <Flex align="center" wrap="wrap" gap={2}>
              {(expandedAuthors[item.id || row.index]
                ? item.authors
                : item.authors.slice(0, 1)
              ).map((authorEmail: string) => (
                <Flex key={authorEmail} align="center">
                  <Avatar size="xs" name={authorEmail} />
                  <Link to={`/contributors/${authorEmail}`}>
                    <Text
                      fontSize="sm"
                      fontFamily="heading"
                      color="text.secondary"
                      fontWeight="medium"
                      ml={2}
                      textDecoration="underline"
                      noOfLines={1}
                    >
                      {authorEmail}
                    </Text>
                  </Link>
                </Flex>
              ))}
            </Flex>

            {item.authors.length > 1 && (
              <Button
                variant="link"
                size="sm"
                color="primary"
                fontWeight="medium"
                w="fit-content"
                p={0}
                h="auto"
                onClick={() =>
                  setExpandedAuthors((prev) => ({
                    ...prev,
                    [item.id || row.index]: !prev[item.id || row.index],
                  }))
                }
              >
                {expandedAuthors[item.id || row.index]
                  ? `Show less`
                  : `Show ${item.authors.length - 1} more`}
              </Button>
            )}
          </Flex>
        )}

        {item?.assignee && (
          <Flex align="center">
            <Avatar size="xs" name={item?.assignee} />
            <Link to={`/contributors/${item?.assignee}`}>
              <Text
                fontSize="sm"
                fontFamily="heading"
                color="text.secondary"
                fontWeight="medium"
                ml={2}
                textDecoration="underline"
                noOfLines={1}
              >
                {item?.assignee}
              </Text>
            </Link>
          </Flex>
        )}
      </Stack>
    </Flex>
  );
};
